import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';

const API_BASE_URL = 'https://dolphin-app-33jp4.ondigitalocean.app';

function EasyTutor() {
  const navigate = useNavigate();
  const [hasRefAccess, setHasRefAccess] = useState(false);

  // ── Mirror Navbar refAccess logic ─────────────────────────────────────────
  useEffect(() => {
    const checkRefAccess = () => {
      try {
        const userData = localStorage.getItem('userData');
        const user = userData ? JSON.parse(userData) : null;
        if (user && (user.role === 'super_admin' || user.email === 'info@easylearn.net.in')) {
          setHasRefAccess(true);
        } else {
          setHasRefAccess(localStorage.getItem('refAccess') === '1');
        }
      } catch {
        setHasRefAccess(false);
      }
    };

    checkRefAccess();

    const pollRefAccess = async () => {
      try {
        const userData = localStorage.getItem('userData');
        if (!userData) return;
        const user = JSON.parse(userData);
        if (!user?.email || user.role === 'super_admin' || user.email === 'info@easylearn.net.in') return;
        const res = await fetch(
          `${API_BASE_URL}/isfathena/user/can-access-reference?email=${encodeURIComponent(user.email)}`
        );
        const data = await res.json();
        const newVal = data.can_access ? '1' : '0';
        if (newVal !== localStorage.getItem('refAccess')) {
          localStorage.setItem('refAccess', newVal);
          setHasRefAccess(data.can_access);
        }
      } catch {}
    };

    const handleStorageEvent = (e) => {
      if (e.key === 'refAccessToggle') {
        try {
          const { email, value } = JSON.parse(e.newValue);
          const userData = localStorage.getItem('userData');
          if (userData) {
            const user = JSON.parse(userData);
            if (user.email === email) {
              localStorage.setItem('refAccess', value);
              setHasRefAccess(value === '1');
            }
          }
        } catch {}
      }
      checkRefAccess();
    };

    const pollInterval = setInterval(pollRefAccess, 10000);
    window.addEventListener('storage', handleStorageEvent);
    window.addEventListener('refAccessChange', checkRefAccess);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('storage', handleStorageEvent);
      window.removeEventListener('refAccessChange', checkRefAccess);
    };
  }, []);

  // ── Protection & Security Setup ──────────────────────────────────────────
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable certain keyboard shortcuts
    const handleKeyDown = (e) => {
      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J')) ||
        (e.ctrlKey && e.key === 'U')
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection and copy
    const handleCopy = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('dragstart', handleDragStart);

    // Disable text selection via CSS
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.msUserSelect = 'none';

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('dragstart', handleDragStart);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      document.body.style.msUserSelect = '';
    };
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  const pricingPlans = [
    {
      id: 1,
      name: 'Regular',
      price: '₹999/year',
      students: 'Up to 10 students',
      description: 'Perfect for new tutors',
      size: 'small'
    },
    {
      id: 2,
      name: 'Silver',
      price: '₹1,999/year',
      students: 'Up to 20 students',
      description: 'Growing tutorial business',
      size: 'medium'
    },
    {
      id: 3,
      name: 'Gold',
      price: '₹2,999/year',
      students: 'Up to 30 students',
      description: 'Established educators',
      size: 'large'
    },
    {
      id: 4,
      name: 'Diamond',
      price: '₹3,999/year',
      students: 'Up to 40 students',
      description: 'Premium tutoring services',
      size: 'xlarge'
    },
    {
      id: 5,
      name: 'Platinum',
      price: '₹4,999/year',
      students: 'Up to 50 students',
      description: 'Elite educational institutions',
      size: 'xxlarge'
    }
  ];

  const getSizeClasses = (size) => {
    switch(size) {
      case 'small':
        return 'w-48 h-32 text-sm';
      case 'medium':
        return 'w-52 h-36 text-base';
      case 'large':
        return 'w-56 h-40 text-base';
      case 'xlarge':
        return 'w-64 h-44 text-lg';
      case 'xxlarge':
        return 'w-72 h-48 text-lg';
      default:
        return 'w-52 h-36';
    }
  };

  return (
    <div className="bg-black text-white min-h-screen overflow-x-hidden w-full">
      {/* Navbar */}
      <Navbar />

      {/* Protection styles for images */}
      <style jsx>{`
        img {
          pointer-events: none;
          user-select: none;
          -webkit-user-drag: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
        }
        html, body {
          overflow-x: hidden;
          max-width: 100%;
        }
      `}</style>

      {/* Background stars effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        {[...Array(80)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-50"
            style={{
              width: Math.random() * 2 + 1 + 'px',
              height: Math.random() * 2 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-36 pb-16 sm:pb-36 px-4 sm:px-8 lg:px-24">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            
            {/* Left Content */}
            <div className="space-y-8 z-10">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold leading-snug">
                EasyTutor: Revolutionizing Online Tutoring with<br />Smart Technology
              </h1>
              
              <p className="text-gray-400 text-base lg:text-lg leading-relaxed">
                Transform your teaching experience with India's most innovative tutoring platform, designed to bridge the gap between traditional education and digital excellence.
              </p>

              <div className="flex flex-wrap gap-3 sm:gap-4">
                <button className="px-6 sm:px-8 py-3 bg-orange-500 text-white rounded-md font-medium hover:bg-orange-600 transition">
                  Start Free Trial
                </button>
                <button className="px-8 py-3 border border-white text-white rounded-md font-medium hover:bg-white hover:text-black transition">
                  Watch Demo
                </button>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative z-10 overflow-hidden rounded-3xl">
              <div className="relative rounded-3xl overflow-hidden border-1 border-cyan-400 shadow-2xl shadow-cyan-500/50 h-64 sm:h-96">
                <img 
                  src="/Assests/home_page_34.png" 
                  alt="EasyTutor Platform" 
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Floating glow effects */}
              <div className="absolute top-0 right-0 w-40 h-40 bg-orange-500/30 rounded-full blur-3xl animate-pulse" />
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-cyan-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
            </div>
            
          </div>
        </div>
      </section>

    {/* The Future of Learning Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 pt-0 pb-20">
        <div className="max-w-7xl mx-auto">
            
            {/* Main Heading */}
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            The Future of Learning<br />is Here
            </h2>

            {/* Subheading */}
            <p className="text-gray-400 text-lg leading-relaxed mb-16 max-w-4xl">
            Welcome to a new era where geographical boundaries dissolve, schedules become flexible, and quality education reaches every corner of the world through intelligent technology.
            </p>

            {/* What is EasyTutor Heading */}
            <h3 className="text-2xl sm:text-4xl font-bold text-white mb-8 sm:mb-12">
            What is EasyTutor?
            </h3>

            {/* Three Cards */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            
            {/* Innovative Platform */}
            <div className="bg-black border-2 border-orange-500 rounded-2xl p-8 hover:bg-gray-900 transition">
                <h4 className="text-2xl font-bold text-white mb-4">
                Innovative Platform
                </h4>
                <p className="text-gray-400 leading-relaxed">
                Revolutionary online tutoring system connecting passionate teachers with eager learners across any distance, breaking traditional classroom limitations.
                </p>
            </div>

            {/* Flexible Learning */}
            <div className="bg-black border-2 border-orange-500 rounded-2xl p-8 hover:bg-gray-900 transition">
                <h4 className="text-2xl font-bold text-white mb-4">
                Flexible Learning
                </h4>
                <p className="text-gray-400 leading-relaxed">
                Experience education that adapts to your schedule - learn anytime, anywhere without compromising on quality or interaction.
                </p>
            </div>

            {/* Group Collaboration */}
            <div className="bg-black border-2 border-orange-500 rounded-2xl p-8 hover:bg-gray-900 transition">
                <h4 className="text-2xl font-bold text-white mb-4">
                Group Collaboration
                </h4>
                <p className="text-gray-400 leading-relaxed">
                Foster meaningful group learning experiences without requiring face-to-face meetings or complex video setups.
                </p>
            </div>

            </div>

        </div>
    </section>

    {/* The Vision Behind EasyTutor Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
            <img 
                src="/Assests/home_page_20.png" 
                alt="Background" 
                className="w-full h-full object-cover opacity-100"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/80"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
            
            {/* Main Heading */}
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-8 sm:mb-12 text-left">
            The Vision Behind EasyTutor
            </h2>

            {/* Two Column Grid */}
            <div className="grid md:grid-cols-2 gap-8 sm:gap-12 mb-8 sm:mb-12">
            
            {/* Education For All */}
            <div>
                <h3 className="text-3xl font-bold text-white mb-6">
                Education For All
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                We believe quality tutoring shouldn't be limited by geography, economic status, or physical constraints. Our platform democratizes access to exceptional education worldwide.
                </p>
            </div>

            {/* Our Mission */}
            <div>
                <h3 className="text-3xl font-bold text-white mb-6">
                Our Mission
                </h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                Empowering every student through personalized, effective learning experiences that adapt to individual needs and learning styles.
                </p>
            </div>

            </div>

            {/* Core Value Box */}
            <div className="bg-orange-900/40 border-l-4 border-orange-500 rounded-lg p-8">
            <div className="flex items-start gap-4">
                <div className="mt-1">
                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                </div>
                <p className="text-gray-200 text-lg leading-relaxed">
                <span className="font-bold">Core Value:</span> Respect for Individuals - we understand that every learner is unique, deserving education tailored to their pace, preferences, and potential.
                </p>
            </div>
            </div>

        </div>
    </section>

    {/* Your Virtual Classroom, Simplified Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
            <img 
                src="/Assests/home_page_9.png" 
                alt="Virtual Classroom Background" 
                className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/70"></div>
        </div>

        <div className="max-w-5xl sm:pl-60 relative z-10 text-left px-4 sm:px-0">
            
            {/* Main Heading */}
            <h2 className="text-4xl lg:text-4xl font-bold text-white mb-6">
                Your Virtual Classroom, Simplified
            </h2>

            {/* Description */}
            <p className="text-gray-300 text-lg sl:text-xl leading-relaxed">
                Experience the power of sophisticated learning technology designed with simplicity in mind. Every tool, every feature, crafted to enhance your teaching without complexity.
            </p>

        </div>
    </section>

        {/* Powerful Features That Transform Tutoring Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
            <img 
                src="/Assests/home_page_7.png" 
                alt="Features Background" 
                className="w-full h-full object-cover opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/70"></div>
        </div>

        <div className="max-w-5xl mx-auto relative z-10 text-center">
            
            {/* Main Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Powerful Features That Transform Tutoring
            </h2>

            {/* Description */}
            <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
                Discover how cutting-edge technology meets intuitive design to create the ultimate tutoring experience for both educators and students.
            </p>

        </div>
    </section>

    {/* Embedded Mentoring Tools Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-20">
        <div className="max-w-7xl mx-auto">
            
            {/* Main Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
                Embedded Mentoring Tools
            </h2>

            {/* Three Features Grid */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
            
                {/* HD Video Conferencing */}
                <div className="text-left space-y-4">
                    {/* Icon */}
                    <div className="flex justify-left mb-6">
                        <div className="w-16 h-16 border-2 border-orange-500 rounded-lg flex items-center justify-center">
                            <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                        </div>
                    </div>
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-white">
                        HD Video Conferencing
                    </h3>
                    {/* Description */}
                    <p className="text-gray-400 leading-relaxed">
                        Crystal-clear, real-time video interaction ensuring seamless communication between tutors and students, fostering genuine connection despite physical distance.
                    </p>
                </div>

                {/* Interactive Whiteboard */}
                <div className="text-left space-y-4">
                    {/* Icon */}
                    <div className="flex justify-left mb-6">
                        <div className="w-16 h-16 border-2 border-orange-500 rounded-lg flex items-center justify-center">
                            <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                        </div>
                    </div>
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-white">
                        Interactive Whiteboard
                    </h3>
                    {/* Description */}
                    <p className="text-gray-400 leading-relaxed">
                        Advanced whiteboard with Geogebra integration for mathematics, physics, and chemistry - making complex concepts visual and engaging.
                    </p>
                </div>

                {/* Smart Tools */}
                <div className="text-left space-y-4">
                    {/* Icon */}
                    <div className="flex justify-left mb-6">
                        <div className="w-16 h-16 border-2 border-orange-500 rounded-lg flex items-center justify-center">
                            <span className="text-orange-500 text-2xl font-serif italic">f/dx</span>
                        </div>
                    </div>
                    {/* Title */}
                    <h3 className="text-2xl font-bold text-white">
                        Smart Tools
                    </h3>
                    {/* Description */}
                    <p className="text-gray-400 leading-relaxed">
                        Built-in math solver, comprehensive file managers, screen recording, and text-to-speech functionality enhance lesson delivery and accessibility.
                    </p>
                </div>

            </div>

        </div>
    </section>

    {/* User-Friendly Interface & Seamless Experience Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
            <img 
                src="/Assests/home_page_35.png" 
                alt="Interface Background" 
                className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/30"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
            
            {/* Main Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
                User-Friendly Interface & Seamless Experience
            </h2>

            {/* Three Cards Grid */}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            
                {/* Minimalist Design */}
                <div className="bg-black/80 border-2 border-orange-500 rounded-2xl p-8 hover:bg-black/80 transition">
                    <h3 className="text-2xl font-bold text-white mb-4">
                        Minimalist Design
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                        Thoughtfully crafted interface displaying only essential buttons, reducing cognitive load and maximizing focus on learning outcomes.
                    </p>
                </div>

                {/* Robust Backend */}
                <div className="bg-black/80 border-2 border-orange-500 rounded-2xl p-8 hover:bg-black/80 transition">
                    <h3 className="text-2xl font-bold text-white mb-4">
                        Robust Backend
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                        Enterprise-grade infrastructure ensures glitch-free performance even with large group sessions, maintaining quality at scale.
                    </p>
                </div>

                {/* 24/7 Reliability */}
                <div className="bg-black/80 border-2 border-orange-500 rounded-2xl p-8 hover:bg-black/80 transition">
                    <h3 className="text-2xl font-bold text-white mb-4">
                        24/7 Reliability
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                        Round-the-clock support and server redundancy guarantee consistent uptime and immediate assistance when needed.
                    </p>
                </div>

            </div>

        </div>
    </section>

    {/* Bringing Lessons to Life Section */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-16 sm:py-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
            <img 
                src="/Assests/home_page_36.png" 
                alt="Lessons Background" 
                className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/70"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10 border border-white rounded-lg p-6 overflow-hidden">
          {/* Black overlay with 50% opacity */}
          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg"></div>

          {/* Content above the overlay */}
          <div className="relative z-10">
            {/* Main Heading */}
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Bringing Lessons to Life
            </h2>

            {/* Description */}
            <p className="text-gray-300 text-lg lg:text-xl leading-relaxed">
              Watch as complex mathematical concepts become clear through interactive visualization, real-time problem solving, and collaborative learning experiences.
            </p>
          </div>
        </div>
    </section>

    <section 
      className="relative flex items-center justify-start bg-cover bg-center"
      style={{
        backgroundImage: "url('/Assests/flexible_plans_bg.jpg')",
      }}
    >
      {/* Content */}
      <div className="relative z-10 w-full px-4 sm:px-8 lg:pl-80 py-12 sm:py-20 text-left">
        
        {/* Main Heading */}
        <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8 leading-tight">
          Flexible Plans Tailored to Your Needs
        </h1>

        {/* Description */}
        <p className="text-gray-300 text-xl leading-relaxed max-w-6xl">
          Whether you're an individual tutor starting your journey or a large institution serving hundreds, 
          we have the perfect plan to match your ambitions.
        </p>

      </div>
    </section>

    {/* PRICING SECTION */}
    <section className="relative px-4 sm:px-8 lg:px-24 py-12 overflow-hidden bg-orange-400/65">
      {/* Animated stars background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(60)].map((_, i) => (
          <div
            key={i}
            className="absolute bg-orange-200 rounded-full opacity-40 animate-pulse"
            style={{
              width: Math.random() * 3 + 1 + 'px',
              height: Math.random() * 3 + 1 + 'px',
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animationDelay: Math.random() * 2 + 's',
              animationDuration: Math.random() * 3 + 2 + 's'
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Main Heading */}
        <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-10 sm:mb-16 text-center">
          EasyTutor Pricing Variants
        </h2>

        {/* Mobile: Card Grid | Desktop: Pyramid Layout */}

        {/* Mobile Grid (visible on small screens) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-4">
          {pricingPlans.map((plan) => (
            <div
              key={plan.id}
              className="bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl shadow-2xl p-6 flex items-center gap-4 hover:scale-105 transition-all duration-300"
            >
              {/* Number Badge */}
              <div className="w-16 h-16 flex-shrink-0 bg-orange-300/40 rounded-xl flex items-center justify-center">
                <span className="text-4xl font-bold text-orange-900/50">{plan.id}</span>
              </div>
              {/* Details */}
              <div className="text-left min-w-0">
                <h3 className="text-xl font-bold text-black mb-0.5 truncate">{plan.name}</h3>
                <p className="text-black font-bold text-base mb-0.5">{plan.price}</p>
                <p className="text-black text-sm border-b border-black/30 pb-0.5 mb-0.5">{plan.students}</p>
                <p className="text-black text-sm">{plan.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Pyramid (hidden on mobile/tablet) */}
        <div className="hidden lg:flex flex-col items-start space-y-6 pl-20">
          {pricingPlans.map((plan, index) => (
            <div
              key={plan.id}
              className="flex items-center gap-6 relative"
              style={{ marginLeft: index * 40 + 'px' }}
            >
              {/* Number Box */}
              <div className={`${getSizeClasses(plan.size)} bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl shadow-2xl flex items-center justify-center hover:scale-105 transition-all duration-300`}>
                <div className="text-5xl lg:text-6xl font-bold text-orange-900/30">
                  {plan.id}
                </div>
              </div>
              {/* Plan Details */}
              <div className="text-left">
                <h3 className="text-2xl lg:text-3xl font-bold text-black mb-1">{plan.name}</h3>
                <p className="text-black font-bold text-lg mb-1">{plan.price}</p>
                <p className="text-black text-base border-b-2 border-black/30 pb-1">{plan.students}</p>
                <p className="text-black text-base mt-1">{plan.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer Note */}
        <div className="mt-10 sm:mt-16 text-center">
          <p className="text-black text-sm sm:text-base lg:text-lg leading-relaxed max-w-5xl mx-auto">
            Every plan includes comprehensive access to Dashboard, Classroom, Courses, Materials, Scheduling, Tools, Feedback systems, and detailed Reports.
          </p>
        </div>
      </div>
    </section>

    {/* Join the Revolution Section */}
<section className="relative px-6 sm:px-10 lg:px-24 py-20 bg-black">
  <div className="max-w-7xl mx-auto">
    
    {/* Section Header */}
    <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
      Join the Digital Learning Revolution with EasyTutor
    </h2>

    <p className="text-gray-400 text-lg mb-12 max-w-3xl">
      The future of personalized education is here. Visit{" "}
      <a 
        href="https://easytutor.net.in/" 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-orange-500 font-semibold hover:underline"
      >
        easytutor.net.in
      </a>{" "}
      to explore how our platform empowers educators and transforms student success.
    </p>

    {/* Cards Grid */}
    <div className="grid md:grid-cols-2 gap-6 mb-10">
      
      {/* Empower Educators Card */}
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-white rounded-2xl p-8 hover:border-orange-500 transition">
        <h3 className="text-2xl font-bold text-white mb-4">
          Empower Educators
        </h3>
        <p className="text-gray-300 leading-relaxed">
          Equip tutors with smart tools that enhance teaching, simplify workflows, and improve engagement.
        </p>
      </div>

      {/* Inspire Learners Card */}
      <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-white rounded-2xl p-8 hover:border-orange-500 transition">
        <h3 className="text-2xl font-bold text-white mb-4">
          Inspire Learners
        </h3>
        <p className="text-gray-300 leading-relaxed">
          Deliver interactive, flexible, and personalized learning experiences that drive real outcomes.
        </p>
      </div>

    </div>

    {/* CTA Buttons */}
    <div className="flex flex-wrap gap-4">

      {/* Reference button — only shown when user has ref access */}
      {hasRefAccess && (
      <button
        onClick={() => navigate('/reffrence?productId=3')}
        className="px-8 py-4 bg-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-orange-600 transition flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
        Reference
      </button>
      )}

      <button className="px-8 py-4 bg-orange-500 text-white text-lg font-semibold rounded-lg hover:bg-orange-600 transition">
        Start Learning
      </button>

      <button className="px-8 py-4 border-2 border-white text-white text-lg font-semibold rounded-lg hover:bg-white hover:border-orange-500 hover:text-orange-500 transition">
        Schedule Demo
      </button>
    </div>

  </div>
</section>


    </div>
  );
}

export default EasyTutor;